import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import styles from "./DepartmentMemberPicker.module.css";
import { FiChevronDown, FiSearch, FiX, FiUser, FiInfo } from "react-icons/fi";
import { useAuth } from "../../../hooks/useAuth";
import { apiService } from "../../../api/apiService";
import UserAvatar from "../UserAvatar/UserAvatar";
import { BRAK_ZASTEPCY, TEKST_BRAKU_OSOBY, czyBrakOsoby } from "../../../utils/hrBrakOsoby";

/*
 * [polecenie właściciela 2026-09-30, A] TRYB WNIOSKU (prop `hrTypeCode`):
 * „zastępcę wybieram w obrębie całego działu, działu nie po wszystkich ludziach z aplikacji — dotyczy to
 * składanych wniosków — dział to nie komórka, tylko cały dział, do którego należę”.
 *  - źródło: GET /hr-requests/substitute-candidates (apiService.getHrPersonCandidates) — serwer liczy dział
 *    osoby wniosku (korzeń gałęzi + komórki) i zakres z DEFINICJI pola; przeglądarka go nie wybiera,
 *  - wpis ręczny WYŁĄCZONY przy zakresie pola „w dziale” (bez „Użyj … jako…”, Enter nic nie robi) — serwer
 *    i tak go odrzuca; przy zakresach „poza działem” i „cała spółka” (np. „Przełożony” w karcie zatrudnienia)
 *    zostaje jak dotąd, bo bramka serwera go tam przyjmuje. Zakres podaje SERWER w odpowiedzi (`zakres`,
 *    `zakresListy`) — ekran go nie zgaduje i nie pisze „z działu” o liście całej spółki,
 *  - `assignmentId` (wniosek ze zlecenia kadr) idzie do serwera — osoba wniosku pochodzi wtedy ze zlecenia,
 *  - „Bez osoby zastępującej” (prop `pozwolBrak`, tylko pola zastępstwa) zapisuje ZNACZNIK braku
 *    (utils/hrBrakOsoby.BRAK_ZASTEPCY), a przycisk pokazuje tę deklarację na stałe — nie wraca do placeholdera.
 * Bez `hrTypeCode` (wydawanie dokumentów, offboarding) zachowanie zostaje takie jak dotąd.
 */

const DepartmentMemberPicker = ({
    value,
    onChange,
    departmentId,
    excludeUserId,
    placeholder = "Wybierz pracownika...",
    disabled = false,
    searchAll = true,
    allowFreeText = true,
    optional = false,
    hrTypeCode,
    fieldName,
    forUserId,
    requestId,
    assignmentId,
    pozwolBrak = false,
}) => {
    const { user } = useAuth();
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    // [A16-17] Stan bledu pobierania listy osob + licznik ponowien (klikniecie 'Ponow' zwieksza licznik,
    // co uruchamia efekt pobierania jeszcze raz).
    const [loadError, setLoadError] = useState(false);
    const [retryTick, setRetryTick] = useState(0);
    const wrapperRef = useRef(null);
    const searchRef = useRef(null);
    // Tryb wniosku: nazwa działu osoby wniosku (null = brak przypisania) i sygnał obcięcia listy do limitu.
    const [dzialWniosku, setDzialWniosku] = useState(null);
    const [obcieto, setObcieto] = useState(false);
    // Tryb wniosku: zakres POLA z definicji (decyduje o wpisie ręcznym — ta sama reguła, co bramka zapisu)
    // i zakres, którym serwer FAKTYCZNIE policzył listę (decyduje o podpowiedzi). null = jeszcze nie wiadomo.
    const [zakresPola, setZakresPola] = useState(null);
    const [zakresListy, setZakresListy] = useState(null);

    const trybWniosku = !!hrTypeCode;
    // Wpis ręczny: poza wnioskiem jak dotąd; we wniosku WYŁĄCZNIE przy zakresie innym niż „w dziale”
    // i dopiero, gdy serwer potwierdził zakres (przed odpowiedzią — bezpiecznie wyłączony).
    const wpisReczny = trybWniosku
        ? (allowFreeText && !!zakresPola && zakresPola !== "department")
        : allowFreeText;
    const listaZDzialu = !trybWniosku || !zakresListy || zakresListy === "department";

    const deptId = departmentId || user?.departments?.[0]?.id || user?.department?.id || null;

    // Tryb „cała firma": fraza idzie do SERWERA (wyszukiwarka osób), więc lista zmienia się
    // razem z wpisywaniem; tryb działu pobiera skład raz.
    const trybSerwerowy = trybWniosku || searchAll || !deptId;
    const frazaSerwera = trybSerwerowy ? search.trim() : "";

    // Fetch members - search all users (server search) or department only
    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        const fetchMembers = async () => {
            try {
                let response;
                if (trybWniosku) {
                    const odp = await apiService.getHrPersonCandidates({
                        typeCode: hrTypeCode,
                        field: fieldName,
                        search: frazaSerwera || undefined,
                        forUserId: forUserId || undefined,
                        requestId: requestId || undefined,
                        assignmentId: assignmentId || undefined,
                    });
                    const dane = odp?.data || {};
                    if (!cancelled) {
                        setLoadError(false);
                        setDzialWniosku(dane.dzial || null);
                        setObcieto(!!dane.obcieto);
                        setZakresPola(dane.zakres || "department");
                        setZakresListy(dane.zakresListy || dane.zakres || "department");
                        setMembers((dane.osoby || []).map((o) => ({
                            id: o.id,
                            name: o.name,
                            position: o.position || "",
                            unit: o.unit || "",
                            email: "",
                            department: "",
                        })));
                    }
                    return;
                }
                if (trybSerwerowy) {
                    // [pkt 9 briefu 2026-09-01] Źródłem jest wspólna wyszukiwarka osób (GET /users/search):
                    // to serwer, jednym predykatem (KontaPracownikow + State), odsiewa konta zablokowane
                    // i techniczne — front tej reguły NIE powtarza. Wcześniej picker czytał surową
                    // kartotekę /admin/users, która CELOWO zwraca wszystkie konta (a nie-administratorowi 403).
                    response = await apiService.searchPeople(frazaSerwera, 50);
                } else {
                    // apiService nie ma surowego .get — ta galaz rzucala TypeError i picker
                    // dzialu zawsze spadal na sciezke bledu. Metoda nazwana + oba ksztalty odpowiedzi.
                    response = await apiService.getDepartmentUsers(deptId, 1, 1000);
                    if (response?.data && !Array.isArray(response.data)) {
                        response = { data: response.data.items || response.data.users || [] };
                    }
                }
                if (!cancelled && response?.data) {
                    setLoadError(false);
                    setMembers(
                        response.data.map((u) => {
                            // /users/search oddaje działy jako listę NAZW; skład działu — jako obiekty.
                            const dzial = u.departments?.[0];
                            return {
                                id: u.id,
                                name: `${u.firstName} ${u.lastName}`,
                                position: u.jobTitle || u.positionName || "",
                                email: u.email || "",
                                department: (typeof dzial === "string" ? dzial : dzial?.name) || u.departmentName || "",
                            };
                        })
                    );
                }
            } catch (error) {
                console.error("Błąd wyszukiwania:", error);
                // [A16-17] BYLO ZLE: gdy pobranie listy osob nie wyszlo (5xx, 429, zerwana siec), komponent
                // po cichu siegal do localStorage pod klucz 'hr_dept_members' - klucz LEGACY, ktorego nic w
                // aplikacji juz nie zapisuje - a gdy tam nic nie bylo, wolal getMockMembers() zwracajace
                // pusta tablice. Zadnego komunikatu, zadnego 'Ponow'.
                // SKUTEK DLA UZYTKOWNIKA: pracownik wypelniajacy wniosek HR i wybierajacy osobe zastepujaca widzial
                // po prostu PUSTA liste. Wygladalo to tak, jakby w dziale nie bylo nikogo do wskazania, i nic
                // nie podpowiadalo, ze to chwilowy blad serwera ani co z tym zrobic.
                // NAPRAWA: zamiast cichego fallbacku ustawiamy stan bledu; lista pokazuje informacje
                // o nieudanym pobraniu i przycisk 'Ponow', ktory pobiera dane jeszcze raz.
                if (!cancelled) {
                    setMembers([]);
                    setLoadError(true);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        // Fraza dla serwera jest opóźniana, żeby każda litera nie była osobnym żądaniem.
        const timer = setTimeout(fetchMembers, frazaSerwera ? 250 : 0);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [deptId, trybSerwerowy, frazaSerwera, retryTick, trybWniosku, hrTypeCode, fieldName, forUserId, requestId, assignmentId]);

    const filtered = useMemo(() => {
        let list = members;
        if (excludeUserId) {
            list = list.filter((m) => m.id !== excludeUserId);
        }
        // Tryb wniosku: frazę filtruje SERWER (także „Nazwisko Imię” i stanowisko) — drugi filtr w przeglądarce
        // wyrzucałby trafienia, których lokalna reguła nie zna.
        if (search.trim() && !trybWniosku) {
            const s = search.toLowerCase().trim();
            list = list.filter(
                (m) =>
                    m.name.toLowerCase().includes(s) ||
                    (m.position || "").toLowerCase().includes(s) ||
                    (m.email || "").toLowerCase().includes(s) ||
                    (m.department || "").toLowerCase().includes(s)
            );
        }
        return list;
    }, [members, excludeUserId, search, trybWniosku]);

    // Check if search text matches any employee
    const hasExactMatch = useMemo(() => {
        if (!search.trim()) return true;
        return filtered.length > 0;
    }, [search, filtered]);

    useEffect(() => {
        const handleClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearch("");
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleClick);
            return () => document.removeEventListener("mousedown", handleClick);
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && searchRef.current) {
            searchRef.current.focus();
        }
    }, [isOpen]);

    const handleToggle = useCallback(() => {
        if (disabled) return;
        setIsOpen((prev) => !prev);
        setSearch("");
    }, [disabled]);

    const handleSelect = useCallback(
        (member) => {
            onChange?.({ id: member.id, name: member.name });
            setIsOpen(false);
            setSearch("");
        },
        [onChange]
    );

    const handleClear = useCallback(
        (e) => {
            e.stopPropagation();
            onChange?.(null);
        },
        [onChange]
    );

    // Tryb wniosku: JAWNA deklaracja (znacznik), nie null — „nie mam zastępcy” to wypełnione pole.
    // Poza wnioskiem zachowanie legacy (null) bez zmian.
    const handleNoSubstitute = useCallback(() => {
        onChange?.(trybWniosku ? BRAK_ZASTEPCY : null);
        setIsOpen(false);
        setSearch("");
    }, [onChange, trybWniosku]);

    const handleFreeText = useCallback(() => {
        if (!wpisReczny || !search.trim()) return;
        onChange?.({ id: null, name: search.trim() });
        setIsOpen(false);
        setSearch("");
    }, [onChange, search, wpisReczny]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter" && wpisReczny && search.trim() && !hasExactMatch) {
            e.preventDefault();
            handleFreeText();
        }
    }, [wpisReczny, search, hasExactMatch, handleFreeText]);

    const getInitials = (name) => {
        if (!name) return "?";
        return name
            .split(" ")
            .map((p) => p[0])
            .join("")
            .toUpperCase()
            .slice(0, 2);
    };

    const displayPlaceholder = optional ? `${placeholder} (opcjonalnie)` : placeholder;
    const wybranoBrak = czyBrakOsoby(value);
    // Opcja „Bez osoby zastępującej”: poza wnioskiem jak dotąd (zawsze), we wniosku tylko przy polach zastępstwa.
    const pokazBrak = trybWniosku ? pozwolBrak : true;
    const podpowiedz = !trybWniosku
        ? "Przeszukaj dział lub wpisz dowolne imię i nazwisko"
        : zakresListy === "company"
            ? "Osoby ze spółki pracownika — albo wpisz imię i nazwisko"
            : zakresListy === "outsideDepartment"
                ? (dzialWniosku
                    ? `Osoby ze spółki spoza działu „${dzialWniosku}” — albo wpisz imię i nazwisko`
                    : "Osoby ze spółki spoza działu pracownika — albo wpisz imię i nazwisko")
                : (dzialWniosku
                    ? `Osoby z działu „${dzialWniosku}” (razem z komórkami)`
                    : "Osoby z działu pracownika (razem z komórkami)");
    const bezDzialu = trybWniosku && listaZDzialu && !loading && !loadError && !dzialWniosku && members.length === 0 && !search.trim();

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <button
                type="button"
                className={`${styles.trigger} ${isOpen ? styles.triggerOpen : ""} ${disabled ? styles.triggerDisabled : ""}`}
                onClick={handleToggle}
            >
                {value && wybranoBrak ? (
                    <span className={styles.selectedValue}>
                        {/* Deklaracja „nie mam zastępcy” — trwały stan pola, bez plakietki wpisu ręcznego. */}
                        <span className={styles.selectedAvatar} aria-hidden="true">—</span>
                        <span className={styles.selectedName}>{TEKST_BRAKU_OSOBY}</span>
                        {/* ✕ wewnątrz przycisku-wyzwalacza: <span role="button">, bo <button> w <button> to niepoprawny HTML. */}
                        <span role="button" tabIndex={0} className={styles.clearBtn} onClick={handleClear}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClear(e); } }}
                            title="Wyczyść" aria-label="Wyczyść">
                            <FiX size={12} />
                        </span>
                    </span>
                ) : value ? (
                    <span className={styles.selectedValue}>
                        {/* [R40 2026-09-13] Zdjęcie wybranej osoby; wpis ręczny (bez id) — inicjały jak dotąd. */}
                        <span className={styles.selectedAvatar}>
                            <UserAvatar userId={value.id} wypelnij dekoracyjny inicjaly={getInitials(value.name)} />
                        </span>
                        <span className={styles.selectedName}>
                            {value.name}
                            {!value.id && <span className={styles.freeTextBadge}>Wpisano ręcznie</span>}
                        </span>
                        {/* ✕ wewnątrz przycisku-wyzwalacza: <span role="button">, bo <button> w <button> to niepoprawny HTML. */}
                        <span role="button" tabIndex={0} className={styles.clearBtn} onClick={handleClear}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClear(e); } }}
                            title="Wyczyść" aria-label="Wyczyść">
                            <FiX size={12} />
                        </span>
                    </span>
                ) : (
                    <span className={styles.placeholder}>
                        <FiUser size={13} />
                        {displayPlaceholder}
                    </span>
                )}
                <FiChevronDown size={14} className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ""}`} />
            </button>

            {isOpen && (
                <div className={styles.dropdown}>
                    <div className={styles.searchHint}>
                        <FiInfo size={11} />
                        <span>{podpowiedz}</span>
                    </div>
                    <div className={styles.searchBox}>
                        <FiSearch size={13} className={styles.searchIcon} />
                        <input
                            ref={searchRef}
                            className={styles.searchInput}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={trybWniosku && listaZDzialu ? "Szukaj osoby z działu..." : "Szukaj pracownika..."}
                        />
                    </div>

                    <div className={styles.list}>
                        {/* No substitute option */}
                        {pokazBrak && (
                            <button
                                type="button"
                                className={`${styles.option} ${styles.optionNoSubstitute} ${wybranoBrak ? styles.optionSelected : ""}`}
                                onClick={handleNoSubstitute}
                                aria-pressed={wybranoBrak}
                            >
                                <span className={styles.optionAvatarNoSubstitute}>—</span>
                                <span className={styles.optionInfo}>
                                    <span className={styles.optionName}>{TEKST_BRAKU_OSOBY}</span>
                                    <span className={styles.optionPosition}>Nie wyznaczam osoby zastępującej</span>
                                </span>
                            </button>
                        )}

                        {loading && (
                            <div className={styles.emptyItem}>Ładowanie...</div>
                        )}

                        {/* [A16-17] Widoczny stan bledu zamiast cichej pustej listy — z akcją „Ponów". */}
                        {!loading && loadError && (
                            <div className={styles.emptyItem}>
                                Nie udało się pobrać listy pracowników.
                                <button
                                    type="button"
                                    className={styles.freeTextBtn}
                                    onClick={() => setRetryTick((t) => t + 1)}
                                >
                                    Ponów
                                </button>
                            </div>
                        )}

                        {bezDzialu && (
                            <div className={styles.emptyItem}>
                                {forUserId
                                    ? "Pracownik nie ma przypisanego działu — wybierz „Bez osoby zastępującej” albo poproś kadry o przypisanie"
                                    : "Nie masz przypisanego działu — wybierz „Bez osoby zastępującej” albo poproś kadry o przypisanie"}
                            </div>
                        )}

                        {!bezDzialu && !loading && !loadError && filtered.length === 0 && !search.trim() && (
                            <div className={styles.emptyItem}>Brak pracowników</div>
                        )}

                        {!loading && !loadError && filtered.length === 0 && search.trim() && (
                            <div className={styles.emptyItem}>
                                {trybWniosku && !wpisReczny ? (
                                    listaZDzialu
                                        ? `Nie znaleziono w dziale „${dzialWniosku || "pracownika"}”`
                                        : `Nie znaleziono „${search}”`
                                ) : wpisReczny ? (
                                    <>
                                        Nie znaleziono „{search}"
                                        <button
                                            type="button"
                                            className={styles.freeTextBtn}
                                            onClick={handleFreeText}
                                        >
                                            {trybWniosku && !pozwolBrak
                                                ? `Użyj „${search}" jako osobę wpisaną ręcznie`
                                                : `Użyj „${search}" jako osobę zastępującą`}
                                        </button>
                                    </>
                                ) : (
                                    "Brak wyników"
                                )}
                            </div>
                        )}

                        {!loading &&
                            filtered.map((m) => (
                                <button
                                    key={m.id}
                                    type="button"
                                    className={`${styles.option} ${value?.id === m.id ? styles.optionSelected : ""}`}
                                    onClick={() => handleSelect(m)}
                                >
                                    <span className={styles.optionAvatar}>
                                        <UserAvatar userId={m.id} wypelnij dekoracyjny inicjaly={getInitials(m.name)} />
                                    </span>
                                    <span className={styles.optionInfo}>
                                        <span className={styles.optionName}>{m.name}</span>
                                        {m.position && (
                                            <span className={styles.optionPosition}>{m.position}</span>
                                        )}
                                        {/* Tryb wniosku: stanowisko · komórka działu. */}
                                        {m.unit && (
                                            <span className={styles.optionPosition}>{m.unit}</span>
                                        )}
                                        {m.department && (
                                            <span className={styles.optionPosition}>{m.department}</span>
                                        )}
                                    </span>
                                </button>
                            ))}

                        {trybWniosku && !loading && !loadError && obcieto && (
                            <div className={styles.emptyItem}>Wpisz nazwisko, aby zawęzić</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export { DepartmentMemberPicker };
export default DepartmentMemberPicker;
